import React, { useContext, useCallback, useMemo, useState, useEffect } from 'react';
import useRouter from 'use-react-router';
import { useTranslation } from 'react-i18next';
import * as api from 'da-pcc-frontend-accountmanagement-api';
import { Select, Grid, InputAdornment } from '@material-ui/core';
import TypeAhead, { TypeAheadItem } from 'da-frontend-shared-ui-components/lib/TypeAhead';
import { Search } from 'da-frontend-shared-ui-components/lib/icons';
import AccountContext from '../Context/AccountContext';
import withAPI, { useApi } from '../api';
import Label from '../Common/Label';
import { CompactLoader } from '../Common/Loader';
import TagManager from 'react-gtm-module';

const DEFAULT_LIMIT = 20;

const styles = {
  root: {
    paddingBottom: '2em'
  },
  dropdown: {
    width: 300
  }
}

/** Retrieve accounts async */
const loadAccounts = (filter: string = undefined) =>
  withAPI(api.AccountsApi).getAccounts({
    limit: DEFAULT_LIMIT,
    filter
  });

/** Retrieve account list */
const useAccountList = (filter: string = undefined) =>
  useApi(api.AccountsApi, api => api.getAccounts({
    limit: DEFAULT_LIMIT,
    filter
  }));



/** Nested account dropdown */
const AccountDropdown = ({ value }: { value: api.Accounts }) => {
  const [ selection ] = useContext(AccountContext);
  const { history }   = useRouter()
  const { results }   = value;
  const [ m ]         = useTranslation("marketsegments");

  /** Navigate to selected account */
  const navigate = useCallback(async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = e.target.value;
    const account  = results.filter(i => i.id === selected)[0];

    // need complete selected account object because account defined above does not contain
    // market object field
    const selectedAccount = await withAPI(api.AccountsApi).getAccountsId({ id:selected });

    // set Google analytics variables
    const gaAccountId = (account && account.id) ? account.id : undefined;
    const gaAccountType = (account && account.type) ? account.type : undefined;
    let gaMarketSegment = (selectedAccount && selectedAccount.market) ? selectedAccount.market : undefined;

    // if selectedAccount has a market segment defined, use translated market segment name
    // for gaMarketSegment value
    if (gaMarketSegment)  {
      const marketSegments = m("applications") as { [segment: string]: string };
      gaMarketSegment = marketSegments[selectedAccount.market];
    }
    
    TagManager.dataLayer({
      dataLayer: {
        accountType: gaAccountType,
        accountId: gaAccountId,
        marketSegment: gaMarketSegment,
      }
    });

    // load selected account in browser
    history.push(`/${account.id}`);
  }, [ results, history ]);

  const accounts = useMemo(() => {
    type AccountWithChildren = api.Account & { children: api.Account[] }

    const accounts: AccountWithChildren[] = [];
    const roots: AccountWithChildren[] = [];
    const map: { [x: string]: number } = {};

    // Initialize map
    for (let i = 0; i < results.length; ++i) {
      map[results[i].id] = i;
      accounts.push({ ...results[i], children: [] });
    }

    // Iterate through each item and add it to its parent
    for (let i = 0; i < results.length; ++i) {
      let node = accounts[i];

      if (node.managingAccountId) {
        const managingIndex = map[node.managingAccountId];

        if (managingIndex !== undefined) {
          accounts[ managingIndex ].children.push(node);
          continue;
        }
      }

      roots.push(node);
    }

    return roots;
  }, [ results ]);

  return (
    <Select native
      id="account-select"
      style={styles.dropdown}
      onChange={navigate}
      defaultValue={selection.account.id}>
      {accounts.map(i => [
        // Managing Account
        <option
          key={i.id}
          value={i.id}
          style={{ fontWeight: 'bold' }}
          onSelect={navigate.bind(i)}>{i.name}</option>,
          
        // Child Accounts
        ...i.children.map(i =>
          <option
            key={i.id}
            value={i.id}
            onSelect={navigate.bind(i)}>&nbsp;&nbsp;&nbsp;{i.name}</option>
        )
      ]).reduce((l, r) => [...l, ...r], [])}
    </Select>
  );
}

/** Account search */
const AccountTypeAhead = ({ value: original }: { value: api.Accounts }) => {
  const { t }                 = useTranslation();
  const [ selection ]         = useContext(AccountContext);
  const { history }           = useRouter()
  const [ value, setResults ] = useState(original);
  const [ search, setSearch ] = useState(selection.account.name);

  useEffect(() => {
    // Skip lookup if this is the same
    // or if there is no search
    if (!search || search === selection.account.name) {
      setResults(original);
    }

    // Execute lookup
    else {
      loadAccounts(search).then(value => setResults(value));
    }
  }, [ search, original, selection.account.name ]);

  return (
    <TypeAhead
      id="account-select"
      style={styles.dropdown}
      defaultValue={selection.account.name}
      onSearch={setSearch}
      onItemClick={account => history.push(`/${account.id}`)}
      items={ value.results.map(l => ({ 
        id: l.id, 
        name: l.name, 
        bold: l.type === api.AccountTypeEnum.Distributor || l.type === api.AccountTypeEnum.Dealer
      })) }
      placeholder={t`accounts.Find Account`}
      InputProps={{
        autoComplete: "off",
        endAdornment: (
          <InputAdornment position="end">
            <Search />
          </InputAdornment>
        )
      }}
    />
  );
}

/**
 * Account lookup/dropdown
 */
const AccountLookup = () => {
  const { t } = useTranslation();
  const value = useAccountList();

  const Selection = ({ total }: { total: number }) =>
    (total > DEFAULT_LIMIT)
      ? <AccountTypeAhead value={value} />
      : <AccountDropdown value={value} />;

  return (
    <Grid container style={styles.root}>
      <Grid item>
        <Label htmlFor="account-select">{t`Change Account`}</Label>
      </Grid>
      <Grid item style={{ paddingLeft: '1em' }}>
        { value 
          ? <Selection total={value.total} /> 
          : <CompactLoader /> 
        }
      </Grid>
    </Grid>
  );
};

export default AccountLookup;